/* istanbul ignore file */
import { useRef, useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import { Box, Stack, Typography } from '@mui/material';
import {
  OPPORTUNITY_STAGES,
  isCurrentOpportunityStage,
  isCompletedOpportunityStage,
  isFailedOpportunityStage,
} from 'domain/sales/opportunity/opportunityService';
import classnames from 'classnames';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftOutlined';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { Button } from 'framework/ui/mui-builder';
import { opportunityStepperSx } from './opportunityPipelineStyles';

const SCROLL_OFFSET = 135;

const OpportunityPipelineStepper = ({
  activeStep,
  handleStep,
  steps,
  isPostponed,
}: {
  activeStep: string;
  handleStep: (step: string) => void;
  steps: Dictionary<any>[];
  isPostponed: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateContainerData = () => {
    const container = stepsRef.current;

    setIsScrollable((container?.scrollWidth || 0) > (container?.getBoundingClientRect()?.width || 0));
  };

  useEffect(() => {
    updateContainerData();

    window.addEventListener('resize', updateContainerData);

    return () => {
      window.removeEventListener('resize', updateContainerData);
    };
  }, [ref]);

  useEffect(() => {
    ref.current
      ?.querySelector('.stepper_step--current')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, []);

  const scrollToLeft = () => {
    stepsRef.current?.scrollTo({
      left: stepsRef.current.scrollLeft - SCROLL_OFFSET,
    });
  };

  const scrollToRight = () => {
    stepsRef.current?.scrollTo({
      left: stepsRef.current.scrollLeft + SCROLL_OFFSET,
    });
  };

  return (
    <Stack
      ref={ref}
      className="opportunity-stepper"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={opportunityStepperSx}
    >
      {isScrollable ? (
        <IconButton className="opportunity-steps__control" onClick={scrollToLeft} disableRipple>
          <ChevronLeftIcon />
        </IconButton>
      ) : null}
      <Box sx={{ width: '100%', maxWidth: isScrollable ? 'calc(100% - 50px)' : '100%' }}>
        <Box ref={stepsRef} className="opportunity-steps">
          <Stack className="steps__wrapper" direction="row">
            {steps.map(({ stepName, stateStatus }) => (
              <Button
                onClick={() => handleStep(stepName)}
                className={classnames('stepper_step', {
                  'stepper_step--completed': isCompletedOpportunityStage(stateStatus),
                  'stepper_step--failed': isFailedOpportunityStage(stateStatus),
                  'stepper_step--current': isCurrentOpportunityStage(stateStatus) && !isPostponed,
                  'stepper_step--active': stepName === activeStep,
                  'stepper_step--postponed': isCurrentOpportunityStage(stateStatus) && isPostponed,
                })}
                disableRipple
                sx={{
                  width: `calc(100% / ${steps.length})`,
                }}
              >
                <Typography component="span" variant="inherit" className="stepper_step-label">
                  {OPPORTUNITY_STAGES[stepName as keyof typeof OPPORTUNITY_STAGES]}
                  {isCompletedOpportunityStage(stateStatus) ? <CheckIcon /> : null}
                  {isCurrentOpportunityStage(stateStatus) && isPostponed ? <PauseCircleOutlineIcon /> : null}
                </Typography>
              </Button>
            ))}
          </Stack>
        </Box>
      </Box>
      {isScrollable ? (
        <IconButton className="opportunity-steps__control" onClick={scrollToRight} disableRipple>
          <ChevronLeftIcon />
        </IconButton>
      ) : null}
    </Stack>
  );
};

export default OpportunityPipelineStepper;
